import type { TextFieldProps } from '@issuemanager/apps/client/components';
import {
  useAllQueries,
  useMonitorUserSingleSelectorLoad,
} from '@issuemanager/apps/client/hooks';
import {
  DEBOUNCE_TIME,
  MAX_CHARACTERS_TO_SEARCH,
  MIN_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import {
  useInfiniteUsers,
  useUsers,
} from '@issuemanager/client/features/users';
import { UserQueryMode } from '@issuemanager/constants';
import { debounce } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExtendedAutocomplete } from '../extended-autocomplete';
import { ExtendedAutocompleteProps } from '../extended-autocomplete/types';

export interface UserSelectorProps
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extends Pick<ExtendedAutocompleteProps<any, any, any, any>, 'readOnly'> {
  value?: string | null;
  initialValue?: string | null;
  onChange: (value: string | null) => void;
  TextFieldProps?: TextFieldProps;
  queryMode?: UserQueryMode;
  issueTypeId?: string;
  issueSubtypeId?: string;
  isVisible?: boolean;
}

export function UserSelector(props: UserSelectorProps) {
  const {
    value,
    onChange,
    initialValue,
    TextFieldProps = {},
    readOnly,
    queryMode,
    issueTypeId,
    issueSubtypeId,
    isVisible = true,
  } = props;

  const { t } = useTranslation(['common']);

  const [filterByText, setFilterByText] = useState('');

  const initialUser = useUsers({
    filter: {
      id: initialValue || undefined,
    },
    enabled: !!initialValue,
    handleError: false,
  });
  const initialUserData = initialUser.data?.data[0];

  const currentUsers = useInfiniteUsers({
    filter: {
      searchTerm: filterByText,
      queryMode,
      issueType: issueTypeId,
      issueSubtype: issueSubtypeId,
    },
    enabled: filterByText.length >= MIN_CHARACTERS_TO_SEARCH,
    handleError: true,
  });

  const monitorOwnerUserSelectorLoad = useMonitorUserSingleSelectorLoad();
  useAllQueries({
    queries: [currentUsers],
    onStart: () => {
      monitorOwnerUserSelectorLoad.start();
    },
    onSuccess: () => {
      monitorOwnerUserSelectorLoad.stop();
    },
    onError: () => {
      monitorOwnerUserSelectorLoad.error();
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleInputChange = useCallback(
    debounce((_, value, reason) => {
      if (reason === 'input') {
        setFilterByText(truncateSearchText(value));
      }
    }, DEBOUNCE_TIME),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScrollEnd = useCallback(
    debounce(() => {
      currentUsers.fetchNextPage();
    }, DEBOUNCE_TIME),
    [],
  );
  // Concat initial user with paginated users
  const options = [
    ...(initialUserData ? [initialUserData] : []),
    ...(currentUsers.data?.pages.map((c) => c.data).flat() ?? []),
  ];
  const uniqueOptions = options.filter(
    (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
  );
  // Find option object from id value
  const option = uniqueOptions.find((option) => option.id === value) ?? null;

  if (!isVisible) return null;

  return (
    <ExtendedAutocomplete
      value={option}
      onChange={(_, value) => {
        onChange(value?.id ?? null);
      }}
      onClear={() => {
        onChange(null);
      }}
      onInputChange={handleInputChange}
      onScrollEnd={handleScrollEnd}
      loading={currentUsers.isLoading}
      options={uniqueOptions}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => `${option.name} <${option.email}>`}
      filterOptions={(options) => options}
      readOnly={readOnly}
      TextFieldProps={{
        ...TextFieldProps,
        placeholder: t('inputs.listBox.placeholder'),
      }}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
    />
  );
}
